/**
 * 廉政协议题 —— PDF 渲染公共工具
 * 供 ExamGrading / AnswerPreviewPage / 跨阶段参考资料 等复用
 *
 * 加载策略：本地依赖优先，CDN 兜底（多源），避免外网超时导致渲染失败
 */
const PDF_VERSION = '2.16.105'
const PDF_CDN_LIST = [
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDF_VERSION}/legacy/build`,
  `https://unpkg.com/pdfjs-dist@${PDF_VERSION}/legacy/build`
]

let _pdfjsReady = null

function configureWorker(lib, base) {
  if (lib && lib.GlobalWorkerOptions && base) {
    lib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.js`
  }
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = resolve
    s.onerror = reject
    document.head.appendChild(s)
  })
}

async function loadPdfjsFromCdnFallback() {
  let lastErr = null
  for (const base of PDF_CDN_LIST) {
    try {
      await loadScript(`${base}/pdf.min.js`)
      const lib = window.pdfjsLib
      if (!lib) throw new Error('pdfjsLib not found on window')
      configureWorker(lib, base)
      return lib
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(`Failed to load pdfjs-dist from CDN: ${lastErr ? lastErr.message : 'unknown error'}`)
}

async function loadPdfjsLib() {
  if (_pdfjsReady) return _pdfjsReady

  _pdfjsReady = (async () => {
    if (window.pdfjsLib) {
      const existing = window.pdfjsLib
      const workerReady = typeof existing.GlobalWorkerOptions?.workerSrc === 'string' && existing.GlobalWorkerOptions.workerSrc.length > 0
      if (!workerReady) configureWorker(existing, PDF_CDN_LIST[0])
      return existing
    }

    // 本地依赖优先：避免外网 CDN 不可达
    try {
      const mod = await import('pdfjs-dist/legacy/build/pdf')
      const lib = mod?.default || mod
      if (lib) {
        try {
          const workerEntry = await import('pdfjs-dist/legacy/build/pdf.worker.entry')
          const workerSrc = workerEntry?.default || workerEntry
          if (lib.GlobalWorkerOptions && workerSrc) lib.GlobalWorkerOptions.workerSrc = workerSrc
        } catch (_) {
          // 本地 worker 加载失败时，退回 CDN worker
          configureWorker(lib, PDF_CDN_LIST[0])
        }
        return lib
      }
    } catch (_) {
      // 忽略，继续走 CDN 兜底
    }

    return loadPdfjsFromCdnFallback()
  })()

  return _pdfjsReady
}

/**
 * 解析廉政协议答案 JSON
 * @param {string|object} val - 答案值，如 '{"agreementUrl":"...","checked":true}'
 * @returns {{ agreementUrl: string, checked: boolean }}
 */
export function parseAgreementAnswer(val) {
  if (!val) return { agreementUrl: '', checked: false }
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val
    return {
      agreementUrl: parsed.agreementUrl || parsed.url || '',
      checked: !!parsed.checked
    }
  } catch {
    return { agreementUrl: '', checked: false }
  }
}

/**
 * 判断 URL 是否为 PDF
 */
function isPdfUrl(url) {
  if (!url) return false
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf')
  } catch {
    return url.toLowerCase().includes('.pdf')
  }
}

/**
 * 将 PDF 渲染到指定容器（Canvas 方式）
 * @param {string} url - PDF 文件地址
 * @param {HTMLElement} container - 容器 DOM 元素
 * @param {(state: 'loading'|'done'|'error') => void} onState - 状态回调
 */
export async function renderPdfToContainer(url, container, onState) {
  if (!url || !container || !isPdfUrl(url)) return

  // 清除之前的 canvas
  container.querySelectorAll('canvas').forEach(c => c.remove())

  onState && onState('loading')
  try {
    const pdfjsLib = await loadPdfjsLib()
    const pdf = await pdfjsLib.getDocument(url).promise
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const scale = (container.clientWidth || 600) / page.getViewport({ scale: 1 }).width
      const viewport = page.getViewport({ scale: Math.min(scale, 2) })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.style.width = '100%'
      canvas.style.height = 'auto'
      canvas.style.display = 'block'
      canvas.style.marginBottom = '8px'
      container.appendChild(canvas)
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
    }
    onState && onState('done')
  } catch (e) {
    console.error('[agreementPdf] PDF render error:', e)
    onState && onState('error')
  }
}
